import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import ErrorModal from '../../../../core/common/ErrorModal';
import SuccessModal from '../../../../core/common/successModal';
import { useDispatch, useSelector } from 'react-redux';
import { setSchoolList } from '../../../../core/data/redux/schoolSlice';
import { getUserDetails } from '../../../../lib/getUserDetails';
import { updateSchoolApi, fetchSchoolApi, createSchoolApi, fetchKitchenTypesApi, fetchSchoolTypesApi, fetchSchoolLocationTypesApi, fetchSchoolAreaTypesApi } from '../../../../core/data/services/schoolService';
import { fetchStatesApi, fetchDistrictsByStateApi, fetchBlocksByDistrictApi, fetchVillagesByBlockAreaApi, getCurrentUserRoleType, getCurrentUserEntity } from '../../../../core/data/services/adminService';
import CustomRadio from '../../../../core/common/commonRadio';
import CommonSelect, { Option } from '../../../../core/common/commonSelect';
import { RootState } from '../../../../core/data/redux/store';

// Enums
export enum SCHOOL_GENDER_TYPE {
  BOYS = 'boys',
  GIRLS = 'girls',
  CO_ED = 'co-ed',
}

export enum SCHOOL_SHIFT {
  GENERAL = 'general',
  EVENING = 'evening',
}

// School interface
interface School {
  id: number;
  name: string;
  schoolCode: string;
  udiseCode: string;
  address: string;
  phone: string;
  shift: SCHOOL_SHIFT;
  gender: SCHOOL_GENDER_TYPE;
  balVatika: boolean;
  primary: boolean;
  upperPrimary: boolean;
  secondary: boolean;
  higherSecondary: boolean;
  logo?: string;
  typeId: number;
  locationTypeId: number;
  areaTypeId: number;
  latitude: number;
  longitude: number;
  blockAreaId: number;
  email?: string;
  website?: string;
  establishedYear?: number;
  principalName?: string;
  kitchenTypeId: number;
  mdmEnrollment: number;
  mdmEnrolled?: boolean;
  villageWardId: number;
  stateId: number;
  districtId: number;
}

// Form data interface
interface SchoolFormData extends Omit<School, 'id'> { }

// Function to clean form data for API
const cleanFormDataForAPI = (formData: SchoolFormData): SchoolFormData => {
  return {
    ...formData,
    name: formData.name.trim(),
    schoolCode: formData.schoolCode.trim(),
    udiseCode: formData.udiseCode.trim(),
    address: formData.address.trim(),
    phone: formData.phone.trim(),
    email: formData.email?.trim() || '',
    website: formData.website?.trim() || '',
    principalName: formData.principalName?.trim() || '',
    establishedYear: Number(formData.establishedYear) || 0,
    typeId: Number(formData.typeId) || 0,
    locationTypeId: Number(formData.locationTypeId) || 0,
    areaTypeId: Number(formData.areaTypeId) || 0,
    latitude: Number(formData.latitude) || 0,
    longitude: Number(formData.longitude) || 0,
    villageWardId: Number(formData.villageWardId) || 0,
    districtId: Number(formData.districtId) || 0,
    blockAreaId: Number(formData.blockAreaId) || 0,
    stateId: Number(formData.stateId) || 0,
    kitchenTypeId: Number(formData.kitchenTypeId) || 0,
    mdmEnrollment: Number(formData.mdmEnrollment) || 0,
    // FIX: keep booleans as actual booleans
    balVatika: Boolean(formData.balVatika),
    primary: Boolean(formData.primary),
    upperPrimary: Boolean(formData.upperPrimary),
    secondary: Boolean(formData.secondary),
    higherSecondary: Boolean(formData.higherSecondary),
    mdmEnrolled: Boolean(formData.mdmEnrolled),
  };
};

// Function to get default form data
const getDefaultFormData = (userEntityData: any): SchoolFormData => {
  console.log('userEntityData:', userEntityData);
  let defaultFormData = {
    name: '',
    schoolCode: '',
    udiseCode: '',
    address: '',
    phone: '',
    shift: SCHOOL_SHIFT.GENERAL,
    gender: SCHOOL_GENDER_TYPE.CO_ED,
    balVatika: false,
    primary: false,
    upperPrimary: false,
    secondary: false,
    higherSecondary: false,
    typeId: 0,
    locationTypeId: 0,
    areaTypeId: 0,
    latitude: 0,
    longitude: 0,
    blockAreaId: 0,
    email: '',
    website: '',
    establishedYear: 0,
    principalName: '',
    kitchenTypeId: 0,
    mdmEnrollment: 0,
    mdmEnrolled: false,
    villageWardId: 0,
    stateId: 0,
    districtId: 0,
  };

  const blockAreaId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.blockAreaId) || 0;
  const isVillageWardAdmin = userEntityData?.[0]?.relatedEntity?.villageWards?.id || 0;
  const isBlockAreaAdmin = userEntityData?.[0]?.relatedEntity?.blockAreas?.id || 0;
  const isDistrictAdmin = userEntityData?.[0]?.relatedEntity?.districts?.id || 0;
  const isStateAdmin = userEntityData?.[0]?.relatedEntity?.states?.id || 0;

  if (isVillageWardAdmin) {
    defaultFormData.villageWardId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.id) || 0;
    defaultFormData.blockAreaId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.blockAreaId) || 0;
    defaultFormData.districtId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.districtId) || 0;
    defaultFormData.stateId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.stateId) || 0;
    return defaultFormData;
  }
  if (isBlockAreaAdmin) {
    defaultFormData.blockAreaId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.blockAreaId) || 0;
    defaultFormData.districtId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.districtId) || 0;
    defaultFormData.stateId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.stateId) || 0;
    return defaultFormData;
  }
  if (isDistrictAdmin) {
    defaultFormData.districtId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.districtId) || 0;
    defaultFormData.stateId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.stateId) || 0;
    return defaultFormData;
  }
  if (isStateAdmin) {
    defaultFormData.stateId = Number(userEntityData?.[0]?.relatedEntity?.villageWards?.stateId) || 0;
    return defaultFormData;
  }

  return { ...defaultFormData, blockAreaId };
};

const AddSchool = () => {
  const routes = all_routes;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = location.pathname.includes('edit-school');
  const schoolData = location.state?.school as School | null;
  const [kitchenTypes, setKitchenTypes] = useState<any>([]);
  const [schoolTypes, setSchoolTypes] = useState<any>([]);
  const [schoolLocationTypes, setSchoolLocationTypes] = useState<any>([]);
  const [schoolAreaTypes, setSchoolAreaTypes] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [districts, setDistricts] = useState<any>([]);
  const [blocks, setBlocks] = useState<any>([]);
  const [villages, setVillages] = useState<any>([]);
  const [selectedState, setSelectedState] = useState<number | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<number | null>(null);
  const [selectedVillage, setSelectedVillage] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const userEntityData = getUserDetails();
  const isBlockAreaAdmin = userEntityData?.[0]?.relatedEntity?.blockAreas?.id ? true : false;
  const isDistrictAdmin = userEntityData?.[0]?.relatedEntity?.districts?.id ? true : false;
  const isStateAdmin = userEntityData?.[0]?.relatedEntity?.states?.id ? true : false;
  const isSuperAdmin = userEntityData?.[0]?.role === "Admin";

  const initialFormData: SchoolFormData = schoolData
    ? { ...schoolData }
    : getDefaultFormData(userEntityData);

  const [formData, setFormData] = useState<SchoolFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<SchoolFormData>>({});
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const userData = getUserDetails();
  const blockAreaId = Number(userData?.[0]?.relatedEntity?.villageWards?.blockAreaId) || 0;

  useEffect(() => {
    if (isEditMode && schoolData) {
      setSelectedState(schoolData.stateId || null);
      setSelectedDistrict(schoolData.districtId || null);
      setSelectedBlock(schoolData.blockAreaId || null);
      setSelectedVillage(schoolData.villageWardId || null);
    }
  }, [isEditMode, schoolData]);

  useEffect(() => {
    fetchKitchenTypesApi().then((data) => {
      setKitchenTypes(data);
      setFormData((prev) => ({ ...prev, kitchenTypeId: Number(prev.kitchenTypeId) || data?.[0]?.id || 0 }));
    });
    fetchSchoolTypesApi().then((data) => {
      setSchoolTypes(data);
      setFormData((prev) => ({ ...prev, typeId: Number(prev.typeId) || data?.[0]?.id || 0 }));
    });
    fetchSchoolLocationTypesApi().then((data) => {
      setSchoolLocationTypes(data);
      setFormData((prev) => ({ ...prev, locationTypeId: Number(prev.locationTypeId) || data?.[0]?.id || 0 }));
    });
    fetchSchoolAreaTypesApi().then((data) => {
      setSchoolAreaTypes(data);
      setFormData((prev) => ({ ...prev, areaTypeId: Number(prev.areaTypeId) || data?.[0]?.id || 0 }));
    });

    const roleType = getCurrentUserRoleType();
    const entity = getCurrentUserEntity();

    if (roleType === 'villageWard' && entity?.villageWards?.id) {
      const state = entity.states ? { id: Number(entity.states.id), name: entity.states.name } : null;
      const district = entity.districts ? { id: Number(entity.districts.id), name: entity.districts.name } : null;
      const blockId = Number(entity.blockAreas?.id || entity.villageWards?.blockAreaId);
      const block = blockId ? { id: blockId, name: entity.blockAreas?.name || '' } : null;
      if (state) setStates([state]);
      if (district) setDistricts([district]);
      if (block) setBlocks([block]);
      setSelectedState(state ? state.id : null);
      setSelectedDistrict(district ? district.id : null);
      setSelectedBlock(block ? block.id : null);
      setSelectedVillage(Number(entity.villageWards.id));
      setFormData((prev) => ({
        ...prev,
        stateId: state ? Number(state.id) : prev.stateId,
        districtId: district ? Number(district.id) : prev.districtId,
        blockAreaId: block ? Number(block.id) : prev.blockAreaId,
        villageWardId: Number(entity.villageWards.id),
      }));
      if (block) {
        fetchVillagesByBlockAreaApi(block.id).then((data) => setVillages(data));
      }
      return;
    }

    if (roleType === 'block' && (entity?.blockAreas?.id || entity?.villageWards?.blockAreaId)) {
      const state = entity.states ? { id: Number(entity.states.id), name: entity.states.name } : null;
      const district = entity.districts ? { id: Number(entity.districts.id), name: entity.districts.name } : null;
      const blockId = Number(entity.blockAreas?.id || entity.villageWards?.blockAreaId);
      const block = blockId ? { id: blockId, name: entity.blockAreas?.name || '' } : null;
      if (state) setStates([state]);
      if (district) setDistricts([district]);
      if (block) setBlocks([block]);
      setSelectedState(state ? state.id : null);
      setSelectedDistrict(district ? district.id : null);
      setSelectedBlock(block ? block.id : null);
      setFormData((prev) => ({
        ...prev,
        stateId: state ? Number(state.id) : prev.stateId,
        districtId: district ? Number(district.id) : prev.districtId,
        blockAreaId: block ? Number(block.id) : prev.blockAreaId,
      }));
      if (block) {
        fetchVillagesByBlockAreaApi(block.id).then((data) => setVillages(data));
      }
      return;
    }

    if (roleType === 'district' && (entity?.districts?.id || entity?.villageWards?.districtId)) {
      const state = entity.states ? { id: Number(entity.states.id), name: entity.states.name } : null;
      const districtId = Number(entity.districts?.id || entity.villageWards?.districtId);
      const district = { id: districtId, name: entity.districts?.name || '' };
      if (state) setStates([state]);
      setDistricts([district]);
      setSelectedState(state ? state.id : null);
      setSelectedDistrict(district.id);
      setFormData((prev) => ({
        ...prev,
        stateId: state ? Number(state.id) : prev.stateId,
        districtId: district.id,
      }));
      fetchBlocksByDistrictApi(district.id).then((data) => setBlocks(data));
      return;
    }

    if (roleType === 'state' && entity?.states?.id) {
      const state = { id: Number(entity.states.id), name: entity.states.name } as any;
      setStates([state]);
      setSelectedState(state.id);
      setFormData((prev) => ({ ...prev, stateId: state.id }));
      fetchDistrictsByStateApi(state.id).then((data) => setDistricts(data));
      return;
    }

    fetchStatesApi().then((data) => setStates(data));
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetchDistrictsByStateApi(selectedState).then((data) => setDistricts(data));
    } else {
      setDistricts([]);
    }
  }, [selectedState]);

  useEffect(() => {
    if (selectedDistrict) {
      fetchBlocksByDistrictApi(selectedDistrict).then((data) => setBlocks(data));
    } else {
      setBlocks([]);
    }
  }, [selectedDistrict]);

  useEffect(() => {
    if (selectedBlock) {
      fetchVillagesByBlockAreaApi(selectedBlock).then((data) => setVillages(data));
    } else {
      setVillages([]);
    }
  }, [selectedBlock]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, type } = target;
    const rawValue = type === 'checkbox' ? target.checked : target.value;

    const normalized =
      name === 'mdmEnrolled'
        ? rawValue === 'true' || rawValue === true
          ? true
          : rawValue === 'false' || rawValue === false
            ? false
            : rawValue
        : rawValue;

    setFormData(prev => ({ ...prev, [name]: normalized }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleLocationChange = (type: 'state' | 'district' | 'block' | 'village', value: number | null) => {
    switch (type) {
      case 'state':
        setSelectedState(value);
        setSelectedDistrict(null);
        setSelectedBlock(null);
        setSelectedVillage(null);
        setFormData(prev => ({ ...prev, stateId: value ? Number(value) : 0, districtId: 0, blockAreaId: 0, villageWardId: 0 }));
        break;
      case 'district':
        setSelectedDistrict(value);
        setSelectedBlock(null);
        setSelectedVillage(null);
        setFormData(prev => ({ ...prev, districtId: value ? Number(value) : 0, blockAreaId: 0, villageWardId: 0 }));
        break;
      case 'block':
        setSelectedBlock(value);
        setSelectedVillage(null);
        setFormData(prev => ({ ...prev, blockAreaId: value ? Number(value) : 0, villageWardId: 0 }));
        break;
      case 'village':
        setSelectedVillage(value);
        setFormData(prev => ({ ...prev, villageWardId: value ? Number(value) : 0 }));
        break;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'School name is required';
    if (!formData.schoolCode.trim()) newErrors.schoolCode = 'School code is required';
    if (!formData.udiseCode.trim()) newErrors.udiseCode = 'UDISE code is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Phone number must be 10 digits';
    if (!formData.locationTypeId || formData.locationTypeId === 0) newErrors.locationTypeId = 'Location type is required';
    if (!formData.areaTypeId || formData.areaTypeId === 0) newErrors.areaTypeId = 'Area type is required';
    if (!formData.kitchenTypeId || formData.kitchenTypeId === 0) newErrors.kitchenTypeId = 'Kitchen type is required';
    if (!formData.mdmEnrollment || formData.mdmEnrollment === 0) newErrors.mdmEnrollment = 'MDM enrollment is required';
    if (!formData.mdmEnrolled) newErrors.mdmEnrolled = 'MDM Enrolled is required';
    if (!formData.latitude || formData.latitude === 0) newErrors.latitude = 'Latitude is required';
    if (!formData.longitude || formData.longitude === 0) newErrors.longitude = 'Longitude is required';

    if (isSuperAdmin) {
      if (!formData.stateId || formData.stateId === 0 || isNaN(formData.stateId)) newErrors.stateId = 'State is required';
      if (!formData.districtId || formData.districtId === 0 || isNaN(formData.districtId)) newErrors.districtId = 'District is required';
      if (!formData.blockAreaId || formData.blockAreaId === 0 || isNaN(formData.blockAreaId)) newErrors.blockAreaId = 'Block is required';
      if (!formData.villageWardId || formData.villageWardId === 0 || isNaN(formData.villageWardId)) newErrors.villageWardId = 'Village is required';
    }
    if (isStateAdmin) {
      if (!formData.districtId || formData.districtId === 0 || isNaN(formData.districtId)) newErrors.districtId = 'District is required';
      if (!formData.blockAreaId || formData.blockAreaId === 0 || isNaN(formData.blockAreaId)) newErrors.blockAreaId = 'Block is required';
      if (!formData.villageWardId || formData.villageWardId === 0 || isNaN(formData.villageWardId)) newErrors.villageWardId = 'Village is required';
    }
    if (isDistrictAdmin) {
      if (!formData.blockAreaId || formData.blockAreaId === 0 || isNaN(formData.blockAreaId)) newErrors.blockAreaId = 'Block is required';
      if (!formData.villageWardId || formData.villageWardId === 0 || isNaN(formData.villageWardId)) newErrors.villageWardId = 'Village is required';
    }
    if (isBlockAreaAdmin) {
      if (!formData.villageWardId || formData.villageWardId === 0 || isNaN(formData.villageWardId)) newErrors.villageWardId = 'Village is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrorModal(false);
    setErrorMessage('');
    setShowSuccessModal(false);
    setSuccessMessage('');

    if (!validateForm()) {
      setErrorMessage('Please fill all required fields correctly.');
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);
    try {
      if (isEditMode && schoolData) {
        const changedFields: any = { id: schoolData.id };
        let anyChange = false;
        const cleanedFormData = cleanFormDataForAPI(formData);

        for (const key in cleanedFormData) {
          const k = key as keyof SchoolFormData;
          const currentValue = cleanedFormData[k];
          const originalValue = (schoolData as any)[k];

          if (typeof currentValue === 'string') {
            const trimmedCurrent = String(currentValue || '').trim();
            const trimmedOriginal = String(originalValue || '').trim();
            if (trimmedCurrent === trimmedOriginal) continue;
          }

          const isChanged = JSON.stringify(currentValue) !== JSON.stringify(originalValue);
          if (isChanged) {
            changedFields[k] = currentValue;
            anyChange = true;
          }
        }

        if (!anyChange) {
          setSuccessMessage('No changes detected.');
          setShowSuccessModal(true);
          setIsLoading(false);
          return;
        }

        await updateSchoolApi(schoolData.id, changedFields);
        setSuccessMessage('School updated successfully!');
      } else {
        const cleanedData = cleanFormDataForAPI(formData);

        // FIX: booleans sent as actual booleans, no Number() conversion
        const apiPayload = {
          ...cleanedData,
          typeId: Number(cleanedData.typeId) || 0,
          locationTypeId: Number(cleanedData.locationTypeId) || 0,
          areaTypeId: Number(cleanedData.areaTypeId) || 0,
          kitchenTypeId: Number(cleanedData.kitchenTypeId) || 0,
          mdmEnrollment: Number(cleanedData.mdmEnrollment) || 0,
          latitude: Number(cleanedData.latitude) || 0,
          longitude: Number(cleanedData.longitude) || 0,
          villageWardId: Number(cleanedData.villageWardId) || 0,
          districtId: Number(cleanedData.districtId) || 0,
          blockAreaId: Number(cleanedData.blockAreaId) || 0,
          stateId: Number(cleanedData.stateId) || 0,
          // FIX: explicit boolean casting — no Number() here
          balVatika: Boolean(cleanedData.balVatika),
          primary: Boolean(cleanedData.primary),
          upperPrimary: Boolean(cleanedData.upperPrimary),
          secondary: Boolean(cleanedData.secondary),
          higherSecondary: Boolean(cleanedData.higherSecondary),
          mdmEnrolled: Boolean(cleanedData.mdmEnrolled),
        };

        await createSchoolApi(apiPayload as any);
        setSuccessMessage('School added successfully!');
        setFormData(getDefaultFormData(userEntityData));
      }

      const updatedSchoolsResponse = await fetchSchoolApi({ blockAreaId });
      const updatedSchools = updatedSchoolsResponse.data;
      dispatch(setSchoolList(updatedSchools));
      setShowSuccessModal(true);
      setErrors({});
    } catch (err: any) {
      console.error('Submission Error:', err);

      let msg = 'Something went wrong. Please try again.';

      if (err.response?.data?.message) {
        if (Array.isArray(err.response.data.message)) {
          const newErrors: Record<string, string> = {};
          err.response.data.message.forEach((msg: string) => {
            if (msg.includes('villageWardId')) newErrors.villageWardId = msg;
            if (msg.includes('districtId')) newErrors.districtId = msg;
            if (msg.includes('blockAreaId')) newErrors.blockAreaId = msg;
            msg = err.response.data.message.join(', ');
          });
          setErrors((prev) => ({ ...prev, ...newErrors }));
        } else {
          msg = err.response.data.message;
        }
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      } else if (err.response?.data?.details) {
        msg = err.response.data.details;
      } else if (err.message) {
        msg = err.message;
      }

      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        const newErrors: Record<string, string> = {};
        if (Array.isArray(apiErrors)) {
          apiErrors.forEach((error: any) => {
            if (error.field) newErrors[error.field] = error.message || 'Invalid value';
          });
          setErrors(newErrors);
        }
        msg = 'Please check the highlighted fields and try again.';
      }

      setErrorMessage(msg);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage('');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
    navigate(routes.schoolGrid);
  };

  // Shift options
  const shiftOptions: Option[] = useMemo(
    () => Object.values(SCHOOL_SHIFT).map((shift) => ({
      value: String(shift),
      label: String(shift).charAt(0).toUpperCase() + String(shift).slice(1),
    })),
    []
  );

  const currentShift: Option | null = useMemo(
    () => formData.shift ? shiftOptions.find((o) => o.value === String(formData.shift)) ?? null : null,
    [formData.shift, shiftOptions]
  );

  const handleShiftSelect = (opt: Option | null) => {
    handleInputChange({ target: { name: 'shift', value: opt?.value ?? '' } } as any);
  };

  // Gender options
  const genderOptions: Option[] = useMemo(
    () => Object.values(SCHOOL_GENDER_TYPE).map((gender) => ({
      value: String(gender),
      label: String(gender).split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('-'),
    })),
    []
  );

  const currentGender: Option | null = useMemo(
    () => formData.gender ? genderOptions.find((o) => o.value === String(formData.gender)) ?? null : null,
    [formData.gender, genderOptions]
  );

  const handleGenderSelect = (opt: Option | null) => {
    handleInputChange({ target: { name: 'gender', value: opt?.value ?? '' } } as any);
  };

  // FIX: Boolean select handlers — set actual booleans directly, not strings
  const balVatikaOptions: Option[] = useMemo(() => [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ], []);

  const currentBalVatika: Option | null = useMemo(() => {
    if (formData.balVatika === true) return balVatikaOptions[0];
    if (formData.balVatika === false) return balVatikaOptions[1];
    return null;
  }, [formData.balVatika, balVatikaOptions]);

  const handleBalVatikaSelect = (opt: Option | null) => {
    setFormData(prev => ({ ...prev, balVatika: opt?.value === 'yes' }));
    setErrors(prev => ({ ...prev, balVatika: '' as any }));
  };

  const primaryOptions: Option[] = useMemo(() => [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ], []);

  const currentPrimary: Option | null = useMemo(() => {
    if (formData.primary === true) return primaryOptions[0];
    if (formData.primary === false) return primaryOptions[1];
    return null;
  }, [formData.primary, primaryOptions]);

  const handlePrimarySelect = (opt: Option | null) => {
    setFormData(prev => ({ ...prev, primary: opt?.value === 'yes' }));
    setErrors(prev => ({ ...prev, primary: '' as any }));
  };

  const upperPrimaryOptions: Option[] = useMemo(() => [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ], []);

  const currentUpperPrimary: Option | null = useMemo(() => {
    if (formData.upperPrimary === true) return upperPrimaryOptions[0];
    if (formData.upperPrimary === false) return upperPrimaryOptions[1];
    return null;
  }, [formData.upperPrimary, upperPrimaryOptions]);

  const handleUpperPrimarySelect = (opt: Option | null) => {
    setFormData(prev => ({ ...prev, upperPrimary: opt?.value === 'yes' }));
    setErrors(prev => ({ ...prev, upperPrimary: '' as any }));
  };

  const secondaryOptions: Option[] = useMemo(() => [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ], []);

  const currentSecondary: Option | null = useMemo(() => {
    if (formData.secondary === true) return secondaryOptions[0];
    if (formData.secondary === false) return secondaryOptions[1];
    return null;
  }, [formData.secondary, secondaryOptions]);

  const handleSecondarySelect = (opt: Option | null) => {
    setFormData(prev => ({ ...prev, secondary: opt?.value === 'yes' }));
    setErrors(prev => ({ ...prev, secondary: '' as any }));
  };

  const higherSecondaryOptions: Option[] = useMemo(() => [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ], []);

  const currentHigherSecondary: Option | null = useMemo(() => {
    if (formData.higherSecondary === true) return higherSecondaryOptions[0];
    if (formData.higherSecondary === false) return higherSecondaryOptions[1];
    return null;
  }, [formData.higherSecondary, higherSecondaryOptions]);

  const handleHigherSecondarySelect = (opt: Option | null) => {
    setFormData(prev => ({ ...prev, higherSecondary: opt?.value === 'yes' }));
    setErrors(prev => ({ ...prev, higherSecondary: '' as any }));
  };

  // Kitchen type options
  const kitchenTypeOptions: Option[] = useMemo(
    () => kitchenTypes.map((t: any) => ({ value: String(t.id), label: t.name })),
    [kitchenTypes]
  );

  const currentKitchenType: Option | null = useMemo(
    () => formData.kitchenTypeId != null
      ? kitchenTypeOptions.find((o) => o.value === String(formData.kitchenTypeId)) ?? null
      : null,
    [formData.kitchenTypeId, kitchenTypeOptions]
  );

  const handleKitchenTypeSelect = (opt: Option | null) => {
    handleInputChange({ target: { name: 'kitchenTypeId', value: opt?.value ?? '', type: 'select-one' } } as any);
  };

  // School type options
  const schoolTypeOptions: Option[] = useMemo(
    () => schoolTypes.map((t: any) => ({ value: String(t.id), label: t.name })),
    [schoolTypes]
  );

  const currentSchoolType: Option | null = useMemo(
    () => formData.typeId != null
      ? schoolTypeOptions.find((o) => o.value === String(formData.typeId)) ?? null
      : null,
    [formData.typeId, schoolTypeOptions]
  );

  const handleSchoolTypeSelect = (opt: Option | null) => {
    handleInputChange({ target: { name: 'typeId', value: opt?.value ?? '', type: 'select-one' } } as any);
  };

  // Location type options
  const locationTypeOptions: Option[] = useMemo(
    () => schoolLocationTypes.map((t: any) => ({ value: String(t.id), label: t.name })),
    [schoolLocationTypes]
  );

  const currentLocationType: Option | null = useMemo(
    () => formData.locationTypeId != null
      ? locationTypeOptions.find(o => o.value === String(formData.locationTypeId)) ?? null
      : null,
    [formData.locationTypeId, locationTypeOptions]
  );

  const handleLocationTypeSelect = (opt: Option | null) => {
    handleInputChange({ target: { name: 'locationTypeId', value: opt?.value ?? '', type: 'select-one' } } as any);
  };

  // Area type options
  const areaTypeOptions: Option[] = useMemo(
    () => schoolAreaTypes.map((t: any) => ({ value: String(t.id), label: t.name })),
    [schoolAreaTypes]
  );

  const currentAreaType: Option | null = useMemo(
    () => formData.areaTypeId != null
      ? areaTypeOptions.find((o) => o.value === String(formData.areaTypeId)) ?? null
      : null,
    [formData.areaTypeId, areaTypeOptions]
  );

  const handleAreaTypeSelect = (opt: Option | null) => {
    handleInputChange({ target: { name: 'areaTypeId', value: opt?.value ?? '', type: 'select-one' } } as any);
  };

  // State options
  const stateOptions: Option[] = useMemo(
    () => states.map((s: any) => ({ value: String(s.id), label: s.name })),
    [states]
  );

  const currentState: Option | null = useMemo(
    () => selectedState != null ? stateOptions.find((o) => o.value === String(selectedState)) ?? null : null,
    [selectedState, stateOptions]
  );

  const handleStateSelect = (opt: Option | null) => {
    handleLocationChange('state', opt?.value ? Number(opt.value) : null);
  };

  // District options
  const districtOptions: Option[] = useMemo(
    () => districts.map((d: any) => ({ value: String(d.id), label: d.name })),
    [districts]
  );

  const currentDistrict: Option | null = useMemo(
    () => selectedDistrict != null ? districtOptions.find((o) => o.value === String(selectedDistrict)) ?? null : null,
    [selectedDistrict, districtOptions]
  );

  const handleDistrictSelect = (opt: Option | null) => {
    handleLocationChange('district', opt?.value ? Number(opt.value) : null);
  };

  // Block options
  const blockOptions: Option[] = useMemo(
    () => blocks.map((b: any) => ({ value: String(b.id), label: b.name })),
    [blocks]
  );

  const currentBlock: Option | null = useMemo(
    () => selectedBlock != null ? blockOptions.find((o) => o.value === String(selectedBlock)) ?? null : null,
    [selectedBlock, blockOptions]
  );

  const handleBlockSelect = (opt: Option | null) => {
    handleLocationChange('block', opt?.value ? Number(opt.value) : null);
  };

  // Village options
  const villageOptions: Option[] = useMemo(
    () => villages.map((v: any) => ({ value: String(v.id), label: v.name })),
    [villages]
  );

  const currentVillage: Option | null = useMemo(
    () => selectedVillage != null ? villageOptions.find((o) => o.value === String(selectedVillage)) ?? null : null,
    [selectedVillage, villageOptions]
  );

  const handleVillageSelect = (opt: Option | null) => {
    handleLocationChange('village', opt?.value ? Number(opt.value) : null);
  };

  const user = useSelector((state: RootState) => state.user.Users);
  const userdetail = useSelector((state: RootState) => state.user.Userdetail);
  const currentUser = user.length > 0 ? user[0] : null;
  const roleName = currentUser?.role || userdetail?.designation || 'No role';

  const getDashboardRoute = (roleRaw: string) => {
    const role = roleRaw.toLowerCase().trim();
    const direct: Record<string, keyof typeof all_routes> = {
      teacher: 'teacherDashboard',
      hm: 'HeadmasterDashboard',
      admin: 'adminDashboard',
      'state admin': 'adminDashboard',
      'district admin': 'adminDashboard',
      'block admin': 'adminDashboard',
      'village admin': 'adminDashboard',
    };
    if (role in direct) return all_routes[direct[role]];
    if (role.includes('teacher')) return all_routes.teacherDashboard;
    if (role.includes('head') || role.includes('hm')) return all_routes.HeadmasterDashboard;
    if (role.includes('admin')) return all_routes.adminDashboard;
    return all_routes['adminDashboard'] ?? '/';
  };

  const dashboardLink = useMemo(() => getDashboardRoute(roleName), [roleName]);

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {isEditMode ? 'Edit School' : 'Add School'}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={dashboardLink}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.schoolGrid}>Schools</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {isEditMode ? 'Edit School' : 'Add School'}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <h4 className="mb-3">{isEditMode ? 'Edit School Details' : 'Add New School'}</h4>

        <form onSubmit={handleSubmit}>
          <div className="card mb-3">
            <div className="card-header">
              <h5>Basic School Details</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">School Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter school name"
                    disabled={isLoading}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">School Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.schoolCode ? 'is-invalid' : ''}`}
                    name="schoolCode"
                    value={formData.schoolCode}
                    onChange={handleInputChange}
                    placeholder="Enter school code"
                    disabled={isLoading}
                  />
                  {errors.schoolCode && <div className="invalid-feedback">{errors.schoolCode}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">UDISE Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.udiseCode ? 'is-invalid' : ''}`}
                    name="udiseCode"
                    value={formData.udiseCode}
                    onChange={handleInputChange}
                    placeholder="Enter UDISE code"
                    disabled={isLoading}
                  />
                  {errors.udiseCode && <div className="invalid-feedback">{errors.udiseCode}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Address <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                    disabled={isLoading}
                  />
                  {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Latitude <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.latitude ? 'is-invalid' : ''}`}
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    placeholder="Enter latitude"
                    disabled={isLoading}
                  />
                  {errors.latitude && <div className="invalid-feedback">{errors.latitude}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Longitude <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.longitude ? 'is-invalid' : ''}`}
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    placeholder="Enter longitude"
                    disabled={isLoading}
                  />
                  {errors.longitude && <div className="invalid-feedback">{errors.longitude}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Phone <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    disabled={isLoading}
                  />
                  {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Shift</label>
                  <CommonSelect
                    options={shiftOptions}
                    defaultValue={currentShift}
                    isDisabled={isLoading}
                    onChange={handleShiftSelect}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Gender</label>
                  <CommonSelect
                    options={genderOptions}
                    defaultValue={currentGender}
                    isDisabled={isLoading}
                    onChange={handleGenderSelect}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    name="email"
                    value={formData.email || ''}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    disabled={isLoading}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Website</label>
                  <input
                    type="text"
                    className={`form-control ${errors.website ? 'is-invalid' : ''}`}
                    name="website"
                    value={formData.website || ''}
                    onChange={handleInputChange}
                    placeholder="Enter website"
                    disabled={isLoading}
                  />
                  {errors.website && <div className="invalid-feedback">{errors.website}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Established Year</label>
                  <input
                    type="number"
                    className={`form-control ${errors.establishedYear ? 'is-invalid' : ''}`}
                    name="establishedYear"
                    value={formData.establishedYear || ''}
                    onChange={handleInputChange}
                    placeholder="Enter established year"
                    disabled={isLoading}
                  />
                  {errors.establishedYear && <div className="invalid-feedback">{errors.establishedYear}</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">
              <h5>School Levels</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Bal Vatika</label>
                  <CommonSelect
                    options={balVatikaOptions}
                    defaultValue={currentBalVatika}
                    isDisabled={isLoading}
                    onChange={handleBalVatikaSelect}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Primary</label>
                  <CommonSelect
                    options={primaryOptions}
                    defaultValue={currentPrimary}
                    isDisabled={isLoading}
                    onChange={handlePrimarySelect}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Upper Primary</label>
                  <CommonSelect
                    options={upperPrimaryOptions}
                    defaultValue={currentUpperPrimary}
                    isDisabled={isLoading}
                    onChange={handleUpperPrimarySelect}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Secondary</label>
                  <CommonSelect
                    options={secondaryOptions}
                    defaultValue={currentSecondary}
                    isDisabled={isLoading}
                    onChange={handleSecondarySelect}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Higher Secondary</label>
                  <CommonSelect
                    options={higherSecondaryOptions}
                    defaultValue={currentHigherSecondary}
                    isDisabled={isLoading}
                    onChange={handleHigherSecondarySelect}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Kitchen Type</label>
                  <CommonSelect
                    options={kitchenTypeOptions}
                    defaultValue={currentKitchenType}
                    isDisabled={isLoading}
                    onChange={handleKitchenTypeSelect}
                  />
                  {errors.kitchenTypeId && <div className="invalid-feedback d-block">{errors.kitchenTypeId}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Mdm Enrolled</label>
                  <div className={`d-flex ${errors?.mdmEnrolled ? 'is-invalid' : ''} gap-2`}>
                    <CustomRadio
                      id="mdmEnrolled-yes"
                      name="mdmEnrolled"
                      checked={formData.mdmEnrolled === true}
                      value={true}
                      label="Yes"
                      onChange={handleInputChange}
                    />
                    <CustomRadio
                      id="mdmEnrolled-no"
                      name="mdmEnrolled"
                      checked={formData.mdmEnrolled === false}
                      value={false}
                      label="No"
                      onChange={handleInputChange}
                    />
                  </div>
                  {errors.mdmEnrolled && <div className="invalid-feedback d-block">{errors.mdmEnrolled}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Mdm Enrollment</label>
                  <input
                    type="text"
                    className={`form-control ${errors?.mdmEnrollment ? 'is-invalid' : ''}`}
                    name="mdmEnrollment"
                    value={formData.mdmEnrollment || ''}
                    onChange={handleInputChange}
                    placeholder="Enter Enrollment Count"
                    disabled={isLoading}
                  />
                  {errors.mdmEnrollment && <div className="invalid-feedback">{errors.mdmEnrollment}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Type</label>
                  <CommonSelect
                    options={schoolTypeOptions}
                    defaultValue={currentSchoolType}
                    isDisabled={isLoading}
                    onChange={handleSchoolTypeSelect}
                  />
                  {errors.typeId && <div className="invalid-feedback d-block">{errors.typeId}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Location Type</label>
                  <CommonSelect
                    className={`${errors.locationTypeId ? 'is-invalid' : ''}`}
                    options={locationTypeOptions}
                    defaultValue={currentLocationType}
                    isDisabled={isLoading}
                    onChange={handleLocationTypeSelect}
                  />
                  {errors.locationTypeId && <div className="invalid-feedback d-block">{errors.locationTypeId}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Area Type</label>
                  <CommonSelect
                    options={areaTypeOptions}
                    defaultValue={currentAreaType}
                    isDisabled={isLoading}
                    onChange={handleAreaTypeSelect}
                  />
                  {errors.areaTypeId && <div className="invalid-feedback d-block">{errors.areaTypeId}</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">
              <h5>School Location</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {isSuperAdmin && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">State <span className="text-danger">*</span></label>
                    <CommonSelect
                      className={`${errors.stateId ? 'is-invalid' : ''}`}
                      options={stateOptions}
                      defaultValue={currentState}
                      isDisabled={isLoading || isStateAdmin}
                      onChange={handleStateSelect}
                    />
                    {errors.stateId && <div className="invalid-feedback d-block">{errors.stateId}</div>}
                  </div>
                )}
                {(isSuperAdmin || isStateAdmin) && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">District <span className="text-danger">*</span></label>
                    <CommonSelect
                      className={`${errors.districtId ? 'is-invalid' : ''}`}
                      options={districtOptions}
                      defaultValue={currentDistrict}
                      isDisabled={isLoading || !selectedState}
                      onChange={handleDistrictSelect}
                    />
                    {errors.districtId && <div className="invalid-feedback d-block">{errors.districtId}</div>}
                  </div>
                )}
                {(isSuperAdmin || isStateAdmin || isDistrictAdmin) && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Block <span className="text-danger">*</span></label>
                    <CommonSelect
                      className={`${errors.blockAreaId ? 'is-invalid' : ''}`}
                      options={blockOptions}
                      defaultValue={currentBlock}
                      isDisabled={isLoading || !selectedDistrict}
                      onChange={handleBlockSelect}
                    />
                    {errors.blockAreaId && <div className="invalid-feedback d-block">{errors.blockAreaId}</div>}
                  </div>
                )}
                {(isSuperAdmin || isStateAdmin || isDistrictAdmin || isBlockAreaAdmin) && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Village/Ward <span className="text-danger">*</span></label>
                    <CommonSelect
                      className={`${errors.villageWardId ? 'is-invalid' : ''}`}
                      options={villageOptions}
                      defaultValue={currentVillage}
                      isDisabled={isLoading || !selectedBlock}
                      onChange={handleVillageSelect}
                    />
                    {errors.villageWardId && <div className="invalid-feedback d-block">{errors.villageWardId}</div>}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <Link
              to={routes.schoolGrid}
              className="btn btn-light me-2"
              onClick={(e) => isLoading && e.preventDefault()}
            >
              Cancel
            </Link>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {isEditMode ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                isEditMode ? 'Update School' : 'Add School'
              )}
            </button>
          </div>
        </form>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={handleCloseErrorModal}
      />
      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={handleCloseSuccessModal}
      />
    </div>
  );
};

export default AddSchool;